import React from 'react';
import { Link } from 'react-router-dom';

function TermsPage() {
  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <nav className="text-sm text-dark-text-secondary mb-6">
        <Link to="/" className="hover:text-dark-text transition-colors">Home</Link>
        <span className="mx-2">›</span>
        <span className="text-dark-text">Terms of Service</span>
      </nav>

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Terms of Service
      </h1>

      <p className="text-sm text-dark-text-secondary mb-8">
        Last updated: February 2025
      </p>

      {/* Content */}
      <div className="prose prose-invert prose-zinc max-w-none space-y-8">
        {/* Section 1 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">1. Introduction</h2>
          <p className="text-zinc-400 leading-relaxed">
            Welcome to DayTradingBench. These Terms of Service ("Terms") govern your access to and use of the DayTradingBench website and services (the "Service") operated by daytradingbench.com ("we", "us", or "our").
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            By accessing or using the Service, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Service.
          </p>
        </section>

        {/* Section 2 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">2. Description of Service</h2>
          <p className="text-zinc-400 leading-relaxed">
            DayTradingBench is a benchmark platform that evaluates large language models (LLMs) on their ability to make trading decisions. The Service:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li>Uses simulated trading with virtual money only</li>
            <li>Does not involve real financial transactions</li>
            <li>Is intended for research, educational, and entertainment purposes</li>
            <li>Displays performance data based on hypothetical trading outcomes</li>
          </ul>
          <p className="text-zinc-400 leading-relaxed mt-3">
            <strong className="text-zinc-300">No real money is traded on this platform.</strong> All balances, profits, and losses shown are simulated.
          </p>
        </section>

        {/* Section 3 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">3. Disclaimer</h2>
          <p className="text-zinc-400 leading-relaxed">
            <strong className="text-zinc-300">The Service does not provide financial advice.</strong> The information presented on DayTradingBench, including model performance data, trading decisions, and any analysis, is provided solely for educational and research purposes.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            You should not rely on any information on this Service as a substitute for professional financial advice. We do not recommend any specific investments, trading strategies, or financial products. Any trading or investment decisions you make are solely your responsibility.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            Past simulated performance of AI models is not indicative of future results in real trading environments.
          </p>
        </section>

        {/* Section 4 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">4. User Obligations</h2>
          <p className="text-zinc-400 leading-relaxed">
            When using the Service, you agree to:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li>Use the Service only for lawful purposes</li>
            <li>Not attempt to gain unauthorized access to any part of the Service</li>
            <li>Not interfere with or disrupt the Service or servers</li>
            <li>Not use automated systems to access the Service without permission</li>
            <li>Not reproduce, distribute, or create derivative works from the Service content without authorization</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">5. Intellectual Property</h2>
          <p className="text-zinc-400 leading-relaxed">
            The Service and its original content, features, and functionality are owned by daytradingbench.com and are protected by international copyright, trademark, and other intellectual property laws.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            The DayTradingBench name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of daytradingbench.com.
          </p>
        </section>

        {/* Section 6 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">6. Service Availability</h2>
          <p className="text-zinc-400 leading-relaxed">
            We strive to maintain the availability of the Service but do not guarantee uninterrupted access. The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            We reserve the right to modify, suspend, or discontinue the Service (or any part thereof) at any time without prior notice.
          </p>
        </section>

        {/* Section 7 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">7. Limitation of Liability</h2>
          <p className="text-zinc-400 leading-relaxed">
            To the fullest extent permitted by applicable law, daytradingbench.com shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
          </p>
          <ul className="list-disc list-inside text-zinc-400 leading-relaxed mt-3 space-y-2">
            <li>Loss of profits or revenue</li>
            <li>Loss of data</li>
            <li>Trading losses or financial damages</li>
            <li>Any other intangible losses</li>
          </ul>
          <p className="text-zinc-400 leading-relaxed mt-3">
            This limitation applies regardless of the legal theory on which the claim is based, even if we have been advised of the possibility of such damages.
          </p>
        </section>

        {/* Section 8 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">8. Changes to Terms</h2>
          <p className="text-zinc-400 leading-relaxed">
            We reserve the right to modify these Terms at any time. We will notify users of significant changes by posting the new Terms on this page and updating the "Last updated" date.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            Your continued use of the Service after any changes constitutes acceptance of the new Terms.
          </p>
        </section>

        {/* Section 9 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">9. Governing Law</h2>
          <p className="text-zinc-400 leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of Finland, without regard to its conflict of law provisions.
          </p>
          <p className="text-zinc-400 leading-relaxed mt-3">
            Any disputes arising from or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of Finland.
          </p>
        </section>

        {/* Section 10 */}
        <section>
          <h2 className="text-xl font-semibold text-zinc-50 mb-3">10. Contact Information</h2>
          <p className="text-zinc-400 leading-relaxed">
            If you have any questions about these Terms, please contact us:
          </p>
          <div className="mt-4 p-4 bg-dark-tertiary border border-dark-border rounded-lg">
            <p className="text-zinc-300 font-medium">daytradingbench.com</p>
            <p className="text-zinc-400 text-sm mt-2">
              Email:{' '}
              <a
                href="mailto:contact@daytradingbench.com"
                className="text-green-500 hover:text-green-400"
              >
                contact@daytradingbench.com
              </a>
            </p>
          </div>
        </section>
      </div>

      {/* Back link */}
      <div className="mt-12 pt-8 border-t border-dark-border">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-dark-text-secondary hover:text-dark-text transition-colors"
        >
          <span>←</span>
          <span>Back to Leaderboard</span>
        </Link>
      </div>
    </div>
  );
}

export default TermsPage;
